import { useMemo } from "react";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { Kicker } from "@/components/ui/kicker";
import { Chip } from "@/components/ui/chip";
import {
  composeWalkthroughTone,
  verdictLabel,
  type VerdictRating,
} from "@/components/experience/vocabulary";
import { toneChipTone } from "@/components/walkthrough-badges";
import { ReasoningText } from "@/components/walkthrough/reasoning-text";

export interface WorkspaceHumanTestBannerProps {
  walkthrough: any;
  verdicts?: Array<{
    question?: string;
    rating?: string;
    verdict?: string;
    reasoning?: string;
  }>;
  scenarioRuns?: any[];
  findingTitles: Map<string, string>;
  projectId: string;
  onOpenFinding: (findingId: string) => void;
}

export function WorkspaceHumanTestBanner({
  walkthrough,
  verdicts,
  scenarioRuns,
  findingTitles,
  projectId,
  onOpenFinding,
}: WorkspaceHumanTestBannerProps) {
  const primaryVerdict = verdicts && verdicts.length > 0 ? verdicts[0] : null;
  const rating = (primaryVerdict?.rating ?? primaryVerdict?.verdict) as
    | VerdictRating
    | undefined;
  const hasVerdict = Boolean(rating || primaryVerdict?.reasoning);
  const personaName = walkthrough?.persona?.name as string | undefined;

  const configuredQuestions = useMemo(() => {
    const rawTests =
      walkthrough?.scenario?.humanTests ??
      walkthrough?.scenario?.human_tests ??
      [];
    const fromScenario: string[] = Array.isArray(rawTests)
      ? rawTests
          .map((t: any) => (typeof t === "string" ? t : t?.question))
          .filter((q): q is string => typeof q === "string" && q.length > 0)
      : [];
    if (fromScenario.length > 0) return fromScenario;

    // Fallback: extract human test questions recorded on sibling runs of this scenario
    const seen = new Set<string>();
    for (const run of scenarioRuns ?? []) {
      if (Array.isArray(run?.verdict)) {
        for (const v of run.verdict) {
          if (typeof v?.question === "string" && v.question.trim().length > 0) {
            seen.add(v.question.trim());
          }
        }
      }
    }
    return Array.from(seen);
  }, [walkthrough?.scenario, scenarioRuns]);

  if (walkthrough?.status !== "completed") {
    return null;
  }

  if (!hasVerdict) {
    return (
      <section
        aria-label="Human test primary verdict"
        className="mb-6 rounded-lg border border-border/80 bg-card p-4 sm:p-5 shadow-2xs space-y-2.5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-2.5">
          <div className="flex items-center gap-2">
            <Kicker as="h2" className="text-xs">
              The human test
            </Kicker>
            <Chip tone="muted">Not evaluated</Chip>
          </div>
          {personaName && (
            <span className="text-xs text-muted-foreground">
              Walked as{" "}
              <strong className="font-medium text-foreground">
                {personaName}
              </strong>
            </span>
          )}
        </div>
        <div className="space-y-1.5">
          {configuredQuestions.length > 0 && (
            <ul className="space-y-1">
              {configuredQuestions.map((question, idx) => (
                <li key={idx} className="text-sm font-semibold text-foreground">
                  {question}
                </li>
              ))}
            </ul>
          )}
          <p className="text-sm text-muted-foreground">
            Human test evaluation was not recorded for this run — re-run
            evaluate or check scenario human_tests configuration.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Human test primary verdict"
      className="mb-6 rounded-lg border border-border/80 bg-card p-4 sm:p-5 shadow-2xs space-y-2.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-2.5">
        <div className="flex items-center gap-2">
          <Kicker as="h2" className="text-xs">
            The human test
          </Kicker>
          {rating && (
            <Chip
              tone={toneChipTone(composeWalkthroughTone("completed", rating))}
            >
              {verdictLabel(rating)}
            </Chip>
          )}
        </div>
        {personaName && (
          <span className="text-xs text-muted-foreground">
            Evaluated as{" "}
            <strong className="font-medium text-foreground">
              {personaName}
            </strong>
          </span>
        )}
      </div>
      <div className="space-y-1">
        {primaryVerdict?.question && (
          <h3 className="text-sm font-semibold text-foreground">
            {primaryVerdict.question}
          </h3>
        )}
        {primaryVerdict?.reasoning && (
          <PersonaVoice
            size="base"
            className="text-foreground max-w-4xl leading-relaxed"
          >
            &ldquo;
            <ReasoningText
              text={primaryVerdict.reasoning}
              findings={findingTitles}
              projectId={projectId}
              walkthroughId={walkthrough?.id}
              onSelectFinding={onOpenFinding}
            />
            &rdquo;
          </PersonaVoice>
        )}
      </div>
    </section>
  );
}
